import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    EyeIcon,
    PencilSquareIcon,
    TrashIcon,
    ShareIcon,
    ChartBarIcon,
    CheckCircleIcon,
    PauseCircleIcon,
} from '@heroicons/react/24/outline';
import { surveyApiService } from '../../api/surveyApi';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import DataTable from '../../components/ui/DataTable';
import TableAction, { TableActionGroup, TableActionDivider } from '../../components/ui/TableAction';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';

const SurveyList = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSurveys = async () => {
            try {
                const response = await surveyApiService.getSurveys();
                const data = Array.isArray(response) ? response : (response?.results || []);
                setSurveys(data);
            } catch (error) {
                console.error('Error fetching surveys:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSurveys();
    }, []);

    const handleDelete = async (slug) => {
        if (window.confirm('Bu anketi silmek istediğinize emin misiniz?')) {
            try {
                await surveyApiService.deleteSurvey(slug);
                setSurveys(surveys.filter(s => s.slug !== slug));
            } catch (error) {
                console.error('Error deleting survey:', error);
                alert('Anket silinemedi.');
            }
        }
    };

    const copyToClipboard = (slug) => {
        const url = `${window.location.origin}/s/${slug}`;
        navigator.clipboard.writeText(url);
        alert('Anket kayıt linki kopyalandı!');
    };

    const columns = [
        {
            header: 'ANKET ADI',
            accessorKey: 'title',
            size: 400,
            cell: ({ row }) => {
                const survey = row.original;
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                            {survey.title?.tr || survey.title?.en || Object.values(survey.title || {})[0] || 'İsimsiz Anket'}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium tracking-tight uppercase tracking-tighter">#{survey.slug}</span>
                    </div>
                );
            }
        },
        {
            header: 'DURUM',
            accessorKey: 'is_active',
            size: 100,
            cellClassName: 'text-center',
            cell: ({ getValue }) => {
                const isActive = getValue();
                return (
                    <div className="flex justify-center">
                        {isActive ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-[10px] font-bold text-emerald-600 dark:text-emerald-500 border border-emerald-100 dark:border-emerald-500/20">
                                <CheckCircleIcon className="w-3.5 h-3.5" />
                                AKTİF
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-slate-50 dark:bg-slate-800 text-[10px] font-bold text-slate-400 border border-slate-100 dark:border-slate-800">
                                <PauseCircleIcon className="w-3.5 h-3.5" />
                                PASİF
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'YANIT',
            accessorKey: 'submission_count',
            size: 80,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <div className="font-bold text-slate-700 dark:text-slate-300">
                    {getValue() || 0}
                </div>
            )
        },
        {
            header: 'VERSİYON',
            accessorKey: 'version',
            size: 80,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <span className="text-[10px] font-bold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-slate-500">
                    v{getValue() || 1}
                </span>
            )
        },
        {
            header: 'OLUŞTURMA',
            accessorKey: 'created_at',
            size: 120,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <div className="text-[11px] font-medium text-slate-500">
                    {getValue() ? format(new Date(getValue()), 'dd.MM.yyyy', { locale: tr }) : '-'}
                </div>
            )
        },
        {
            header: 'İŞLEMLER',
            id: 'actions',
            size: 175,
            cellClassName: 'text-center',
            cell: ({ row }) => {
                const survey = row.original;
                return (
                    <TableActionGroup>
                        <TableAction
                            icon={ShareIcon}
                            onClick={() => copyToClipboard(survey.slug)}
                            color="emerald"
                            title="Linki Kopyala"
                        />
                        <TableAction
                            icon={EyeIcon}
                            onClick={() => navigate(`/survey/submissions/${survey.slug}`)}
                            color="blue"
                            title="Yanıtları Gör"
                        />
                        <TableAction
                            icon={ChartBarIcon}
                            onClick={() => navigate(`/survey/analytics/${survey.slug}`)}
                            color="indigo"
                            title="Analiz"
                        />
                        <TableAction
                            icon={PencilSquareIcon}
                            onClick={() => navigate(`/survey/edit/${survey.slug}`)}
                            color="amber"
                            title="Düzenle"
                        />
                        <TableActionDivider />
                        <TableAction
                            icon={TrashIcon}
                            onClick={() => handleDelete(survey.slug)}
                            color="rose"
                            title="Sil"
                        />
                    </TableActionGroup>
                );
            }
        }
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 dark:border-slate-800 pb-8">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight italic">Anket Yönetimi</h1>
                    <p className="text-sm text-slate-500 font-medium tracking-tight uppercase tracking-widest text-[10px]">Sistem üzerindeki tüm anketlerin yönetim merkezi</p>
                </div>
            </div>

            <ActionToolbar>
                <IconActionButton
                    icon={PlusIcon}
                    onClick={() => navigate('/survey/create')}
                    title="Yeni Anket"
                />
            </ActionToolbar>

            <DataTable
                data={surveys}
                columns={columns}
                isLoading={loading}
            />
        </div>
    );
};

export default SurveyList;
